/*
  Snusify: the sticky header turns periwinkle as soon as the page scrolls.

  Concept paints the header from two custom properties on its section wrapper,
  --color-background and --color-foreground. At the top of the page (over the
  hero) the editor's settings apply: white bar, navy text. Once the visitor has
  scrolled past a small threshold the wrapper is given the brand periwinkle and
  white text, and everything in the bar — links, icons, logo, cart badge — goes
  white with it (see snusify-brand-home.css). Scrolling back to the top
  restores the white bar. The colour is read from the kit token --blue-300 so
  the bar stays in sync with the section blocks.
*/
(() => {
  const headerSection = document.querySelector('.header-section.shopify-section-group-header-group, .header-section');
  if (!headerSection) return;

  const THRESHOLD = 24; // px scrolled before the bar tints

  const channels = (color) => (color.match(/[\d.]+/g) || []).map(Number);

  const periwinkle = () => {
    const token = getComputedStyle(document.documentElement).getPropertyValue('--blue-300').trim();
    const m = token.match(/^#([0-9a-f]{6})$/i);
    if (m) return [1, 3, 5].map((i) => parseInt(m[1].slice(i - 1, i + 1), 16)).join(' ');
    const rgb = channels(token);
    return rgb.length >= 3 ? rgb.slice(0, 3).join(' ') : '127 156 255';
  };

  let tinted = null;

  const update = () => {
    syncRadius();
    const shouldTint = window.scrollY > THRESHOLD;
    if (shouldTint === tinted) return;
    tinted = shouldTint;

    if (shouldTint) {
      headerSection.style.setProperty('--color-background', periwinkle());
      headerSection.style.setProperty('--color-foreground', '255 255 255');
      headerSection.classList.add('snusify-nav-tint');
    } else {
      headerSection.style.removeProperty('--color-background');
      headerSection.style.removeProperty('--color-foreground');
      headerSection.classList.remove('snusify-nav-tint');
    }
  };

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      update();
    });
  };

  // Mirror the header block's real corner radius onto a custom property so the
  // cart panel can take exactly the same rounding (see snusify-brand-home.css).
  const header = headerSection.querySelector('.header');
  const syncRadius = () => {
    if (!header) return;
    // Concept squares the header block off once the page is scrolled; the
    // panel keeps the resting radius, so only a non-zero reading is stored.
    const r = getComputedStyle(header, '::before').borderTopLeftRadius;
    if (r && parseFloat(r) > 0) headerSection.style.setProperty('--snusify-header-radius', r);
    // Where the header ends on screen: the cart panel caps its height so it
    // never runs past the bottom of the viewport from there.
    headerSection.style.setProperty('--snusify-header-bottom', `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px`);
  };
  syncRadius();
  addEventListener('resize', syncRadius);

  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('page:loaded', schedule);
  // The "Deals" menu item is dressed as a price ribbon (snusify-brand-home.css).
  // Tagged by label so it survives menu reordering.
  const tagDeals = () => {
    headerSection.querySelectorAll('.header__menu .menu__item').forEach((item) => {
      const label = item.querySelector('.btn-text[data-text]') || item;
      if (/^\s*deals\s*$/i.test(label.textContent)) item.classList.add('snusify-nav-deal');
    });
  };
  // Ribbon treatment retired: Deals renders like every other menu link.
  // (tagDeals kept for a quick re-enable.)

  // Exposed so the state can be recomputed on demand (used when verifying
  // the theme headlessly, where animation frames do not run).
  window.snusifyNavTint = update;
  update();
})();
